import { ofType } from 'redux-observable';
import { mergeMap, withLatestFrom, filter } from 'rxjs/operators';

import {
    DISABLED_VARIANT,
    ENABLED_VARIANT,
    backendABTestMap,
} from '@perpay-web/constants/experiments';
import { AB_TESTS_ENDPOINT } from '@perpay-web/constants/urls';
import {
    replaceABTest,
    reportABTest,
    fetchABTestError,
} from '@perpay-web/fintech/actions/shared/abTests';
import {
    BACKEND_FETCH_AB_TEST,
    BACKEND_VERIFY_AB_TEST,
    STORE_AB_TEST_REPLACE,
} from '@perpay-web/fintech/constants/actionTypes';
import {
    fetchABTests as fetchABTestsAction,
    fetchABTestsError,
    fetchABTestsSuccess,
} from '@perpay-web/fintech/dataModules/fetchABTests';
import { getABTests } from '@perpay-web/fintech/dataModules/fetchABTests';
import { getABTestEnabled } from '@perpay-web/fintech/selectors/shared/abTests';
import { handleErrorMessageWithFallback } from '@perpay-web/observable/operators/handleErrorMessageWithFallback';
import {
    analyticsMarkAuthenticatedAbTestVariant,
    getTestNameFromResponse,
} from '@perpay-web/services/abTesting';

export const fetchABTests = (action$, state$, { get }) =>
    action$.pipe(
        ofType(fetchABTestsAction().type),
        mergeMap(({ payload }) => get(`${AB_TESTS_ENDPOINT}${payload}/`)),
        withLatestFrom(state$),
        mergeMap(([results, state]) => {
            const abTests = getABTests(state);
            const { response } = results;

            const testName = Object.keys(response).filter(
                (key) => key !== 'userExistedInGroup',
            )[0];
            const { userExistedInGroup } = response;
            const enabled = response[testName];
            const testData = { enabled, userExistedInGroup };

            const updatedABTests = { ...abTests, [testName]: testData };

            const returnActions = [fetchABTestsSuccess(updatedABTests)];

            if (!userExistedInGroup) {
                returnActions.push(reportABTest({ enabled, testName }));
            }

            return returnActions;
        }),
        handleErrorMessageWithFallback((error) => [fetchABTestsError(error)]),
    );

export const fetchABTestsV2 = (action$) =>
    action$.pipe(
        ofType(fetchABTestsSuccess().type),
        mergeMap((results) => {
            const { payload } = results;

            const testName = getTestNameFromResponse(payload);
            const { userExistedInGroup } = payload;
            const enabled = payload[testName];

            const returnActions = [];
            if (!userExistedInGroup) {
                returnActions.push(reportABTest({ enabled, testName }));
            }

            return returnActions;
        }),
    );

export function getABTest(action$, state$, { get }) {
    return action$.pipe(
        ofType(BACKEND_FETCH_AB_TEST, BACKEND_VERIFY_AB_TEST),
        mergeMap((action) => {
            const { type, payload } = action;
            let endpoint = `${AB_TESTS_ENDPOINT}${payload}/`;

            if (type === BACKEND_VERIFY_AB_TEST) {
                endpoint = `${endpoint}?verify_company=true`;
            }

            return get(endpoint);
        }),
        mergeMap((results) => [replaceABTest(results.response)]),
        handleErrorMessageWithFallback((error) => [fetchABTestError(error)]),
    );
}

export function reportABTestVariant(action$, state$) {
    return action$.pipe(
        ofType(STORE_AB_TEST_REPLACE),
        // Only fire analytics event when user is being
        // assigned to an A/B test group
        filter((action) => !action.payload.userExistedInGroup),
        withLatestFrom(state$),
        mergeMap(([action, state]) => {
            const { payload } = action;
            const testName = Object.keys(payload).filter(
                (key) => key !== 'userExistedInGroup',
            )[0];
            const abTestEnabled = getABTestEnabled(state, testName);
            // Pinwheel AB test is an exception to typical AB test behavior
            const isPinwheelABTest = testName === 'pinwheel';
            return !isPinwheelABTest
                ? [
                      analyticsMarkAuthenticatedAbTestVariant({
                          experiment: backendABTestMap[testName],
                          variant: abTestEnabled
                              ? ENABLED_VARIANT
                              : DISABLED_VARIANT,
                      }),
                  ]
                : [];
        }),
    );
}
